import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, ListChecks } from 'lucide-react'
import { fetchContent, has, text } from '../api/content'

/** The five files, in the order somebody would collect them. */
const STEPS = [
  { key: 'import.guide.families', label: 'Families' },
  { key: 'import.guide.students', label: 'Students' },
  { key: 'import.guide.classes', label: 'Classes' },
  { key: 'import.guide.staff', label: 'Staff' },
  { key: 'import.guide.enrollments', label: 'Enrolments' },
] as const

/**
 * What to download from Jackrabbit, on the screen where it is needed.
 *
 * **Why it exists.** The exports have to be regenerated by hand for years, and
 * an export produced with the wrong columns looks identical to a correct one
 * until somebody counts the roster. The contract lived only in
 * `docs/JACKRABBIT-EXPORTS.md`, which is the wrong place for something a person
 * needs while standing in Jackrabbit with an empty folder (D-144).
 *
 * **Why the copy is content, not markup.** These instructions describe
 * *Jackrabbit's* interface, which nobody here controls. When they move a menu
 * the fix has to be editable text rather than a deploy (A-16, NFR-14). Every
 * line below comes from a `ContentBlock`.
 *
 * **Collapsed by default**, because it is read carefully once and skipped
 * forever after — and the drop zone is what the screen is for.
 */
export default function ImportGuide() {
  const [open, setOpen] = useState(false)
  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })

  // Nothing to show before the baseline has run. Silence beats an empty box.
  if (!has(content, 'import.guide.heading')) return null

  return (
    <section className="mb-5 overflow-hidden rounded-2xl border border-line bg-surface">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-2.5 bg-transparent px-4 py-3 text-left"
      >
        <ListChecks size={16} strokeWidth={2} className="shrink-0 text-accent" />

        <span className="min-w-0 flex-1">
          <span className="block text-[14px] font-semibold">
            {text(content, 'import.guide.heading')}
          </span>
          {has(content, 'import.guide.intro') && !open && (
            <span className="mt-0.5 block truncate text-[12.5px] text-ink-3">
              Five files &middot; four grid exports and one report
            </span>
          )}
        </span>

        <span className="shrink-0 text-ink-3">
          {open ? <ChevronDown size={16} strokeWidth={2} /> : <ChevronRight size={16} strokeWidth={2} />}
        </span>
      </button>

      {open && (
        <div className="border-t border-line px-4 py-4">
          {has(content, 'import.guide.intro') && (
            <p className="mb-4 max-w-[46rem] text-[13px] leading-relaxed text-ink-2">
              {text(content, 'import.guide.intro')}
            </p>
          )}

          <ol className="m-0 flex list-none flex-col gap-2.5 p-0">
            {STEPS.map((step, i) => (
              <li
                key={step.key}
                className="flex gap-3 rounded-xl border border-line-2 bg-surface-2 px-3.5 py-3"
              >
                {/*
                  Numbered because this genuinely is a sequence somebody works
                  through, not decoration on a list of five equal things.
                */}
                <span className="mt-px shrink-0 text-[12px] font-bold text-ink-3 tabular">
                  {i + 1}
                </span>

                <span className="min-w-0">
                  <span className="block text-[12.5px] font-semibold">{step.label}</span>
                  <span className="mt-0.5 block text-[12.5px] leading-relaxed text-ink-2 wrap-anywhere">
                    {text(content, step.key)}
                  </span>
                </span>
              </li>
            ))}
          </ol>

          {has(content, 'import.guide.warning') && (
            <p className="mt-4 rounded-xl border border-warn bg-warn-bg px-3.5 py-2.5 text-[12.5px] leading-relaxed">
              {text(content, 'import.guide.warning')}
            </p>
          )}

          <p className="mt-3 text-[11.5px] text-ink-3">
            This text is editable under &ldquo;Words on your website&rdquo;.
          </p>
        </div>
      )}
    </section>
  )
}
